import React, { useState, useRef, useEffect } from "react";
import { Target, UserPlus, Pencil, Check, X, CalendarPlus } from "lucide-react";
import { fmt } from "../lib/format.js";
import {
  KartaPrehledu, HlavickaDlazdice, SkupinaPrehledu, AkceSkupiny,
} from "./PrehledDlazdice.jsx";

// České názvy měsíců
const MONTH_NAMES = [
  "", "leden", "únor", "březen", "duben", "květen", "červen",
  "červenec", "srpen", "září", "říjen", "listopad", "prosinec",
];

function formatMonth(monthKey) {
  if (!monthKey) return "";
  const [y, m] = monthKey.split("-").map(Number);
  return `${MONTH_NAMES[m]} ${y}`;
}

// Jeden cíl: pruh skupiny s tužkou, pod ním procenta, splněno z cíle a proužek.
// Obsah i chování editace zůstávají stejné, mění se jen tvar.
function GoalRow({ label, icon: Icon, current, goal, suffix, onSave, readOnly = false, kompaktni = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(String(goal || 0));
    setEditing(true);
    setConfirming(false);
  };

  const cancel = () => { setEditing(false); setConfirming(false); };

  const requestConfirm = () => {
    const num = parseInt(draft.replace(/\s/g, ""), 10);
    if (isNaN(num) || num < 0) return;
    setConfirming(true);
  };

  const confirmSave = async () => {
    const num = parseInt(draft.replace(/\s/g, ""), 10);
    if (isNaN(num) || num < 0) return;
    setSaving(true);
    try { await onSave(num); } finally {
      setSaving(false); setEditing(false); setConfirming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") requestConfirm();
    if (e.key === "Escape") cancel();
  };

  const pct = goal > 0 ? Math.round((current / goal) * 100) : 0;
  // Barva drží dnešní význam: do padesáti rudá, do osmdesáti žlutá, nad zelená.
  const barvaPruhu = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-rose-400";

  // Kompaktní tvar pro sloučenou dlaždici v CEO přehledu: jeden řádek s
  // popiskem, procentem a proužkem, místo vlastního fialového pruhu a velkého
  // čísla. Editace se chová stejně (14.9.2026).
  if (kompaktni && !editing) {
    return (
      <div className="flex items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 first:border-t-0 sm:px-5">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-slate-700">{label}</span>
          <span className="block truncate text-[11px] font-semibold tabular-nums text-slate-400">
            {fmt(current)} z {fmt(goal)} {suffix}
          </span>
        </span>
        <span className="w-16 shrink-0">
          <span className="block text-right text-sm font-bold tabular-nums text-slate-900">{pct} %</span>
          {goal > 0 && (
            <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-slate-200">
              <span className={`block h-full rounded-full transition-all ${barvaPruhu}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </span>
          )}
        </span>
        {!readOnly && <AkceSkupiny ikona={Pencil} popis="Upravit cíl" onClick={startEdit} />}
      </div>
    );
  }

  return (
    <>
      {!kompaktni && (
        <SkupinaPrehledu
          ikona={Icon}
          nazev={label}
          akce={!readOnly && !editing ? (
            <AkceSkupiny ikona={Pencil} popis="Upravit cíl" onClick={startEdit} />
          ) : undefined}
        />
      )}
      <div className="px-4 py-3 sm:px-5">
        {kompaktni && <div className="mb-1.5 text-[13px] font-semibold text-slate-700">{label}</div>}
        {editing && !confirming && (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              className="field w-full font-semibold tabular-nums"
            />
            <span className="flex-shrink-0 text-xs text-slate-500">{suffix}</span>
            <button
              type="button"
              onClick={requestConfirm}
              aria-label="Potvrdit"
              className="min-h-[44px] rounded-xl bg-emerald-100 p-1 text-emerald-700 sm:min-h-0 sm:hover:bg-emerald-200"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={cancel}
              aria-label="Zrušit"
              className="min-h-[44px] rounded-xl bg-slate-100 p-1 text-slate-500 sm:min-h-0 sm:hover:bg-slate-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {confirming && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-2">
            <p className="mb-1.5 text-xs text-amber-800">
              Změnit na <span className="font-bold">{fmt(parseInt(draft.replace(/\s/g, ""), 10))} {suffix}</span>?
            </p>
            <div className="flex gap-2">
              <button onClick={confirmSave} disabled={saving} className="btn btn-primary">
                {saving ? "…" : "Potvrdit"}
              </button>
              <button onClick={cancel} className="btn btn-secondary">Zrušit</button>
            </div>
          </div>
        )}

        {!editing && (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="text-2xl font-bold leading-none tabular-nums text-slate-900 sm:text-3xl">
                {pct} %
              </span>
              <span className="text-sm font-semibold tabular-nums text-slate-500">
                {fmt(current)} z {fmt(goal)} {suffix}
              </span>
            </div>
            {goal > 0 && (
              <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full rounded-full transition-all ${barvaPruhu}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// `vnorene` kreslí jen obsah bez vlastní karty a hlavičky. Používá to CEO
// přehled, kde cíle sedí ve stejné dlaždici pod prioritami měsíce (14.9.2026).
export default function GoalsCard({ monthlyGoal, monthlyRevenue, monthlyGoalPct, newClientGoal, newClientRevenue, newClientGoalPct, onSaveMonthlyGoal, onSaveNewClientGoal, isNewMonth, month, readOnly = false, vnorene = false }) {
  const Obal = vnorene ? React.Fragment : KartaPrehledu;
  return (
    <Obal>
      {vnorene ? (
        <SkupinaPrehledu ikona={Target} nazev="Cíle" />
      ) : (
        <HlavickaDlazdice
          ikona={Target}
          nazev="Cíle"
          odznak={month ? formatMonth(month) : undefined}
        />
      )}

      {/* Banner: nový měsíc, cíle ještě nenastaveny. Je to pokyn od aplikace,
          ne varování, proto značková fialová, ne modrá (ta v paletě nic neznamená). */}
      {isNewMonth && !readOnly && (
        <div className="border-b border-brand/15 bg-brand-light px-4 py-3 sm:px-5">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-brand-950">
            <CalendarPlus className="h-4 w-4 shrink-0" />
            Nový měsíc, nastav cíle pro {formatMonth(month)}
          </div>
          <p className="text-xs text-brand-deep">
            Klikni na tužku u každého cíle a zadej hodnotu.
          </p>
        </div>
      )}

      <GoalRow
        label="Měsíční revenue"
        icon={Target}
        current={monthlyRevenue}
        goal={monthlyGoal}
        suffix="Kč"
        onSave={onSaveMonthlyGoal}
        readOnly={readOnly}
        kompaktni={vnorene}
      />
      <GoalRow
        label="Revenue nových klientů"
        icon={UserPlus}
        current={newClientRevenue}
        goal={newClientGoal}
        suffix="Kč"
        onSave={onSaveNewClientGoal}
        readOnly={readOnly}
        kompaktni={vnorene}
      />
    </Obal>
  );
}
